import clsx from 'clsx';
import { format } from 'date-fns';
import { toNumber } from 'lodash';
import { oneOf } from 'prop-types';
import { createContext, useEffect, useMemo } from 'react';
import { FormProvider, useForm } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import { useDispatch, useSelector } from 'react-redux';
import { useLocation, useNavigate, useSearchParams } from 'react-router-dom';

import { AuthLayout, Banner, ForgotPasswordForm, LoginForm, RegistrationForm, UpdatePasswordForm } from 'components';
import { processString, redirectToBusinessDomain } from 'helpers';
import { useFetch, useSafeState, useUser } from 'hooks';
import { routes } from 'router';
import { appService } from 'services';
import { addStep, setAutolog } from 'store';

export const AuthContext = createContext();

const AuthPage = ({ type }) => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const location = useLocation();
  const [searchParams, setSearchParams] = useSearchParams();
  const isRedirection = appService.isRedirection();
  const getBusinessNames = appService.getBusinessNames();
  const { currentUser, resetAllStores } = useUser();

  const autolog = useSelector((state) => state.auth.autolog);

  const [isLoading, setIsLoading] = useSafeState(false);
  const [error, setError] = useSafeState({
    login: '',
    registration: ''
  });
  const [isValidBusinessName, setIsValidBusinessName] = useSafeState(true);

  const [requestPassword, setRequestPassword] = useSafeState({ success: false });
  const [requestUpdatePassword, setRequestUpdatePassword] = useSafeState({ success: false });
  const { call: logoutCall } = useFetch({ withSetState: false });
  const { call: loginCall, loading: loginLoading } = useFetch();
  const { call: askAccessCall, loading: askAccessLoading } = useFetch({ withSetState: false });

  const { call: passwordRequestCall } = useFetch({ withSetState: false });
  const { call: updatePasswordCall } = useFetch({ withSetState: false });

  const { call: validationCall } = useFetch({ withSetState: false });

  const defaultValues = useMemo(() => {
    switch (type) {
      case 'login':
        return { userId: '', password: '', rememberMe: false };
      case 'forgotPassword':
        return { userId: '' };
      case 'registration':
        return {
          adhesionType: '',
          employementStatus: '',
          membershipFee: '',
          birthdate: null,
          civility: '',
          company: null,
          email: '',
          firstname: '',
          lastname: '',
          mobilePhone: '',
          newsletter: false,
          paymentMethod: '',
          sponsorshipCode: '',
          userId: ''
        };
      case 'arrivajRegistration':
        return {
          birthdate: null,
          civility: '',
          email: '',
          firstname: '',
          lastname: '',
          phone: '',
          newsletter: false
        };
      case 'updatePassword':
        return { newPassword: '' };
      default:
        return { email: '', password: '' };
    }
  }, [type]);

  const { register, handleSubmit, reset, watch, clearErrors, trigger, getValues, setValue, formState, control } =
    useForm({ mode: 'onBlur', defaultValues });

  const submitClasses = useMemo(() => {
    return clsx('btn secondary mx-auto mt-6 lg:large', {
      'cursor-not-allowed': !formState.isValid || isLoading
    });
  }, [isLoading, formState.isValid]);

  useEffect(() => {
    if (location.pathname === routes.registration.path) {
      setError((prev) => ({ ...prev, login: '', registration: '' }));
    }
  }, [location.pathname]);

  useEffect(() => {
    return () => resetForm();
  }, [type, defaultValues]);

  useEffect(() => {
    setError((prev) => ({
      ...prev,
      login: autolog === 'failed' ? t('auth:login.invalidCredentials') : ''
    }));
  }, [autolog]);

  const resetForm = () => {
    setRequestPassword({ success: false });
    reset({ ...defaultValues });
  };

  const checkUserValidity = async () => {
    const {
      userId: id,
      lastname,
      firstname,
      birthdate,
      email,
      employementStatus,
      sponsorshipCode,
      newsletter,
      company
    } = getValues();

    const companyId = !company?.value ? 1 : toNumber(company?.value) === 1 ? 1 : 2;

    // Remove all spaces from userId
    const userId = id.replace(/\s/g, '');

    const { data } = await validationCall(
      {
        service: 'castelis',
        path: `/users/validate/${userId}`,
        method: 'put',
        query: {
          isLaPoste: employementStatus === 'activePostalWorker' ? 'oui' : 'non',
          userId,
          name: processString(lastname),
          firstname: processString(firstname),
          birthDate: format(new Date(birthdate), 'dd/MM/yyyy'),
          email,
          companyId,
          sponsorshipCode,
          newsletters: newsletter ? 'oui' : 'non'
        }
      },
      { hideToast: true }
    );

    return data;
  };

  const logoutUser = async () => {
    if (!currentUser?.token) return;
    try {
      setIsLoading(true);
      const { data } =
        (await logoutCall({
          service: 'castelis',
          path: '/authentication/logout',
          method: 'post',
          query: { token: currentUser.token }
        })) || {};
      if (data?.status === 200) resetAllStores();
    } finally {
      setIsLoading(false);
    }
  };

  useEffect(() => {
    const handleInvalidBusinessName = async () => {
      if (!isValidBusinessName && currentUser) {
        await logoutUser();
        setError((prev) => ({ ...prev, login: t('auth:login.invalidBusinessName') }));
      }
    };
    handleInvalidBusinessName();
  }, [isValidBusinessName, currentUser]);

  const logUser = async (formValues) => {
    dispatch(setAutolog({ type: 'failed', value: false }));
    try {
      setIsValidBusinessName(true);
      setError((prev) => ({ ...prev, login: '' }));
      const { data } =
        (await loginCall(
          {
            service: 'castelis',
            path: '/authentication/login',
            method: 'post',
            rememberMe: formValues.rememberMe,
            query: {
              login: formValues.userId,
              password: formValues.password
            }
          },
          { hideToast: true }
        )) || {};

      if (data?.status === 400) {
        const errorMessage = data?.body || t('auth:login.invalidCredentials');
        setError((prev) => ({ ...prev, login: errorMessage }));
      } else if (data?.status === 200) {
        checkUserValidBusinessName(data.body.businessName);

        // GTM event : user_cookie_loaded
        window.dataLayer.push({
          event: 'user_cookie_loaded'
        });
      }
    } catch (error) {
      setError((prev) => ({ ...prev, login: t('auth:login.invalidCredentials') }));
    }
  };

  /**
   * @description Handle redirection to TOULOISIRS/ARRIVAJ
   * depending on the user's business name
   * @param {string} businessName
   */
  const checkUserValidBusinessName = (businessName) => {
    let validBusinessName = false;

    getBusinessNames.forEach((element) => {
      if (Object.values(element).includes(businessName)) {
        validBusinessName = true;
      }
    });

    if (validBusinessName) {
      // Redirect to business domain if origin is redirect.arrivaj

      if (isRedirection) {
        redirectToBusinessDomain(businessName);
      } else {
        // Redirect to either home page or the page the user was trying to access

        const targetPath = location.state?.from
          ? `${location.state.from.pathname}${location.state.from.search}`
          : routes.home.path;

        navigate(targetPath, { replace: true });
      }
    } else {
      /* If the business name is not valid, user is not allowed to access the platform
       * Set the error message and logout the user
       */
      setIsValidBusinessName(false);
    }
  };

  const requestNewPassword = async ({ userId: id }) => {
    // Replace all spaces from userId
    const userId = id.replace(/\s/g, '');
    try {
      setIsLoading(true);
      if (appService.isArrivaj()) {
        const subdomain = appService.getCSE();
        await passwordRequestCall(
          {
            service: 'access',
            path: `/${subdomain}/reset-password`,
            method: 'post',
            data: {
              identifier: userId
            }
          },
          { hideToast: true }
        );
      } else {
        await passwordRequestCall(
          {
            service: 'castelis',
            path: `/authentication/forgot/${userId}`,
            method: 'post',
            headers: {
              'Content-Type': null
            }
          },
          { hideToast: true }
        );
      }
      setRequestPassword((prev) => ({ ...prev, success: true }));
    } catch {
      setRequestPassword((prev) => ({ ...prev, success: false }));
    } finally {
      setIsLoading(false);
    }
  };

  const updatePassword = async () => {
    const params = new URLSearchParams(location.search);

    try {
      setIsLoading(true);
      await updatePasswordCall({
        service: 'access',
        path: `/${appService.getCSE()}/update-password`,
        method: 'post',
        data: {
          token: params.get('token'),
          newPassword: getValues('newPassword')
        }
      });
      setRequestUpdatePassword((prev) => ({ ...prev, success: true, error: false }));
    } catch {
      setRequestUpdatePassword((prev) => ({ ...prev, success: false, error: true }));
    } finally {
      setIsLoading(false);
    }
  };

  const registrationUser = async (_, currentStep) => {
    try {
      setIsLoading(true);

      if (currentStep === 'postalUserInfoStep' || currentStep === 'otherUserInfoStep') {
        const data = await checkUserValidity();
        if (data?.status === 200) {
          dispatch(addStep('confirmationStep'));
          navigate({ pathname: routes.registration.path, search: '?step=confirmation' });
        } else {
          setError((prev) => ({ ...prev, registration: t('dialog:validationError') }));
        }
      } else {
        const data = await checkUserValidity();

        if (data?.status === 200) {
          if (currentStep === 'userInfoWithoutMembershipStep') {
            dispatch(addStep('membershipSubscriptionStep'));
          } else {
            setSearchParams({ ...searchParams, step: 'confirmation-other' });
          }
        } else {
          setError((prev) => ({ ...prev, registration: t('dialog:validationError') }));
        }
      }
    } finally {
      setIsLoading(false);
    }
  };

  const arrivajRegistrationUser = async () => {
    const subdomain = appService.getCSE();
    const civility = getValues('civility') === 'Mr' ? 1 : 2;

    const { data } =
      (await askAccessCall({
        service: 'access',
        method: 'post',
        path: `${subdomain}/ask-access`,
        data: {
          civility: civility,
          lastName: getValues('lastname'),
          firstName: getValues('firstname'),
          birthDate: format(new Date(getValues('birthdate')), 'yyyy-MM-dd'),
          email: getValues('email'),
          phone: getValues('phone'),
          newsletter: getValues('newsletter')
        }
      })) || {};

    if (data?.status === 201) {
      dispatch(addStep('confirmationStep'));
      navigate({ pathname: routes.registration.path, search: '?step=confirmation' });
    } else if (data?.status === 409) {
      setError((prev) => ({ ...prev, registration: t('dialog:awaitingActivation') }));
    } else {
      setError((prev) => ({ ...prev, registration: t('dialog:arrivajValidationError') }));
    }
  };

  const formProviderValues = useMemo(() => {
    let submitCallback;

    switch (type) {
      case 'login':
        submitCallback = logUser;
        break;
      case 'forgotPassword':
        submitCallback = requestNewPassword;
        break;
      case 'registration':
        submitCallback = registrationUser;
        break;
      case 'arrivajRegistration':
        submitCallback = arrivajRegistrationUser;
        break;
      case 'updatePassword':
        submitCallback = updatePassword;
        break;
      default:
        break;
    }

    return {
      trigger,
      setValue,
      getValues,
      clearErrors,
      watch,
      register,
      formState,
      isLoading,
      loginLoading,
      askAccessLoading,
      submitClasses,
      onSubmit: handleSubmit(submitCallback),
      request: type === 'forgotPassword' ? requestPassword : requestUpdatePassword,
      control
    };
  }, [type, formState.isValid, isLoading, loginLoading, formState.errors]);

  return (
    <AuthContext.Provider value={{ error, setError }}>
      <AuthLayout type={type}>
        {(error.login || error.registration) && (
          <Banner type="error" className="mb-6" dataTestId="validationError">
            <p> {error.login || error.registration}</p>
          </Banner>
        )}

        <FormProvider {...formProviderValues}>
          {type === 'login' && <LoginForm />}
          {type === 'forgotPassword' && <ForgotPasswordForm />}
          {(type === 'registration' || type === 'arrivajRegistration') && <RegistrationForm />}
          {type === 'updatePassword' && <UpdatePasswordForm />}
        </FormProvider>
      </AuthLayout>
    </AuthContext.Provider>
  );
};

AuthPage.propTypes = {
  type: oneOf(['login', 'forgotPassword', 'registration', 'arrivajRegistration', 'updatePassword']).isRequired
};

export default AuthPage;
